import axios from 'axios';

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터 - JWT 토큰 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    // localStorage에서 토큰 가져오기
    const token = localStorage.getItem('token');
    
    // 토큰이 있으면 Authorization 헤더에 추가
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 에러 처리
    if (error.response) {
      // 서버가 응답을 반환한 경우
      console.error('Response error:', error.response.data);
      
      // 401 Unauthorized - 토큰 만료 또는 유효하지 않음
      if (error.response.status === 401) {
        console.error('인증 실패: 로그인이 필요합니다.');
        // 토큰 제거
        localStorage.removeItem('token');
        localStorage.removeItem('isLoggedIn');
        // 로그인 페이지로 리다이렉트
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      
      // 403 Forbidden - 권한 없음
      if (error.response.status === 403) {
        console.error('권한 없음: 접근이 거부되었습니다.');
      }
    } else if (error.request) {
      // 요청은 보냈지만 응답을 받지 못한 경우
      console.error('Request error:', error.request);
    } else {
      // 요청 설정 중 에러가 발생한 경우
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API 함수들
export const customerAPI = {
  // 고객 조회 (휴대폰 번호)
  checkByPhone: (phone) => apiClient.get('/customers/check', { params: { phone } }),
  
  // 모든 고객 목록 조회
  getAll: () => apiClient.get('/customers'),
  
  // 특정 고객 조회
  getById: (id) => apiClient.get(`/customers/${id}`),

  // 고객 정보 수정
  update: (id, data) => apiClient.put(`/customers/${id}`, data),

  // 고객 검색
  search: (query) => apiClient.get('/customers/search', { params: { query } }),
};

export const reservationAPI = {
  // 예약 생성
  create: (reservationData) => apiClient.post('/reservations', reservationData),

  // 예약 목록 조회
  getAll: () => apiClient.get('/reservations'),

  // 예약 상세 조회
  getById: (id) => apiClient.get(`/reservations/${id}`),

  // 예약 수정
  update: (id, reservationData) => apiClient.put(`/reservations/${id}`, reservationData),

  // 예약 상태 변경
  updateStatus: (id, status) => apiClient.patch(`/reservations/${id}/status`, { status }),

  // 예약 삭제
  delete: (id) => apiClient.delete(`/reservations/${id}`),

  // 가용 시간 슬롯 조회
  getAvailableSlots: (date) => apiClient.get('/reservations/available-slots', { params: { date } }),

  // 전화번호로 본인 예약 조회 (셀프 조회)
  getMyReservations: (phone) => apiClient.get('/reservations/my-reservations', { params: { phone } }),

  // 셀프 예약 취소
  selfCancel: (id, data) => apiClient.post(`/reservations/${id}/self-cancel`, data),

  // 토큰 기반 예약 취소 (카카오 알림톡 취소 버튼용)
  cancelByToken: (token) => apiClient.post(`/reservations/cancel`, null, { params: { token } }),
};

export const smsAPI = {
  // SMS 인증 코드 발송
  sendVerification: (phone) => apiClient.post('/sms/send', { phoneNumber: phone }),

  // SMS 인증 코드 검증
  verifyCode: (phone, code) => apiClient.post('/sms/verify', { phoneNumber: phone, verificationCode: code }),

  // SMS 인증 정보 삭제 (관리자용)
  deleteVerification: (phone) => apiClient.delete(`/sms/verification/${phone}`),
};

export const authAPI = {
  // 로그인
  login: (credentials) => apiClient.post('/auth/login', credentials),

  // 토큰 검증
  verify: () => apiClient.get('/auth/verify'),
};

export const careReportAPI = {
  // 케어 리포트 목록 조회 (고객별)
  getAll: (customerId) => apiClient.get('/care-reports', { params: { customerId } }),

  // 케어 리포트 생성
  create: (reportData) => apiClient.post('/care-reports', reportData),

  // 케어 리포트 생성 + 패키지 차감
  createWithPackage: (reportData) => apiClient.post('/care-reports/with-package', reportData),
};

export const packageAPI = {
  // 패키지 구매
  purchase: (data) => apiClient.post('/packages', data),

  // 패키지 차감 (별도)
  deduct: (id, data) => apiClient.post(`/packages/${id}/deduct`, data),

  // 고객의 패키지 목록
  getByCustomer: (customerId) => apiClient.get('/packages', { params: { customerId } }),

  // 사용 가능한 패키지
  getActive: (customerId) => apiClient.get('/packages/active', { params: { customerId } }),

  // 패키지 상세
  getById: (id) => apiClient.get(`/packages/${id}`),

  // 사용 이력
  getHistory: (id) => apiClient.get(`/packages/${id}/history`),

  // 패키지 수정
  update: (id, data) => apiClient.put(`/packages/${id}`, data),

  // 패키지 삭제
  delete: (id) => apiClient.delete(`/packages/${id}`),

  // 환불 금액 계산
  getRefundCalculation: (id) => apiClient.get(`/packages/${id}/refund-calculation`),

  // 패키지 환불
  refund: (id, data) => apiClient.post(`/packages/${id}/refund`, data),

  // 패키지 거래 이력 (통합 이력)
  getTransactions: (id) => apiClient.get(`/packages/${id}/transactions`),

  // 전체 거래 이력 (통합 이력 페이지용)
  getAllTransactions: (params) => apiClient.get('/packages/transactions/all', { params }),

  // ===== 패키지 공유 관련 API =====

  // 패키지 공유 설정
  sharePackage: (packageId, data) => apiClient.post(`/packages/${packageId}/share`, data),

  // 패키지 공유 해제
  unsharePackage: (shareId) => apiClient.delete(`/packages/shares/${shareId}`),

  // 패키지 공유 목록 조회
  getPackageShares: (packageId) => apiClient.get(`/packages/${packageId}/shares`),

  // 고객이 사용 가능한 모든 패키지 조회 (본인 + 공유받은)
  getAvailablePackages: (customerId) => apiClient.get('/packages/available', { params: { customerId } }),
};

export const notificationAPI = {
  // 모든 알림 조회
  getAll: () => apiClient.get('/notifications'),

  // 특정 고객의 알림 조회
  getByCustomer: (customerId) => apiClient.get(`/notifications/customer/${customerId}`),

  // 알림 통계
  getStats: () => apiClient.get('/notifications/stats'),

  // 알림 발송
  send: (notificationData) => apiClient.post('/notifications/send', notificationData),

  // D-1 리마인더 발송
  triggerReminders: () => apiClient.post('/notifications/trigger-reminders'),
};

export const statisticsAPI = {
  // 오늘 요약 통계
  getTodaySummary: () => apiClient.get('/statistics/today-summary'),

  // 대시보드 통계
  getDashboard: () => apiClient.get('/statistics/dashboard'),

  // 매출 통계
  getRevenue: (params) => apiClient.get('/statistics/revenue', { params }),

  // 예약 상태별 통계
  getReservationStatus: () => apiClient.get('/statistics/reservation-status'),
};

export const treatmentAPI = {
  // 활성화된 시술 목록 조회 (예약 페이지용 - 인증 불필요)
  getActive: () => apiClient.get('/treatments'),

  // 전체 시술 목록 조회 (관리자용)
  getAll: () => apiClient.get('/treatments/admin'),

  // 시술 ID로 조회
  getById: (id) => apiClient.get(`/treatments/${id}`),

  // 시술 생성
  create: (data) => apiClient.post('/treatments', data),

  // 시술 수정
  update: (id, data) => apiClient.put(`/treatments/${id}`, data),

  // 시술 삭제
  delete: (id) => apiClient.delete(`/treatments/${id}`),

  // 이벤트 가격 활성화/비활성화 토글
  toggleEvent: (id) => apiClient.put(`/treatments/${id}/toggle-event`),

  // 활성화/비활성화 토글
  toggleActive: (id) => apiClient.put(`/treatments/${id}/toggle-active`),

  // 카테고리별 시술 조회
  getByCategory: (category) => apiClient.get(`/treatments/category/${category}`),
};

export default apiClient;
